import React, { useMemo, useState } from 'react';
import {
  validateActivityPayload,
  ExploraLiveStage,
  applyExploraSkin,
  type ExploraPayload,
  type ExploraVariable,
  type ExploraSkin,
  type ExploraRuntime,
  type SandboxPayload,
} from '@interactive-room';
import { Link } from 'react-router-dom';
import { emptySandboxPayload } from '../emptyPayloads';
import {
  createDraftId,
  saveActivityDraft,
  type ActivityDraft,
} from '../activityStorage';
import { StudioLiveShell } from '../components/StudioLiveShell';
import { resolveStudioLaunchDefaults } from '../studioLaunch';
import { generateSubjectAwareActivityDraft } from '../studioActivityGeneration';
import type { StudioLiveEditorProps } from '../types/editor.types';

function isExplora(p: SandboxPayload): p is ExploraPayload {
  return p.variant === 'balanza' || p.variant === 'fracciones' || p.variant === 'grafico';
}

function editorRuntime(payload: ExploraPayload): ExploraRuntime {
  const inverse = payload.balanceMode === 'inverse-operations' || Boolean(payload.equationLeft && payload.equationRight);
  return {
    kind: 'explora',
    currentValue: inverse ? (payload.equationRight?.constants ?? payload.targetValue) : payload.initialValue,
    history: [],
    status: inverse ? 'PLAYING' : payload.initialValue === payload.targetValue ? 'SUCCESS' : 'PLAYING',
    equationLeft: payload.equationLeft,
    equationRight: payload.equationRight,
    currentEquation: payload.displayEquation,
  };
}

interface ExploraLiveEditorProps extends StudioLiveEditorProps {}

export function ExploraLiveEditor({
  draftId,
  embedded,
  onSaved,
  launchSubjectId,
  launchPresetId,
}: ExploraLiveEditorProps) {
  const launch = resolveStudioLaunchDefaults(emptySandboxPayload, {
    draftId,
    launchSubjectId,
    launchPresetId,
  });
  const [payload, setPayload] = useState<SandboxPayload>(() => launch.payload);
  const [subjectTag, setSubjectTag] = useState(() => launch.subjectTag);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [savedId] = useState(draftId ?? createDraftId());

  if (!isExplora(payload)) {
    return (
      <div className="rounded-2xl border border-amber-500/40 bg-amber-950/20 p-6 text-sm text-amber-200">
        El mercado inflacionario aún usa el editor clásico.{' '}
        <Link to="/studio/sandbox" className="underline">
          Cambiar variante
        </Link>
      </div>
    );
  }

  const runtime = useMemo(() => editorRuntime(payload), [payload]);

  const patch = (p: Partial<ExploraPayload>) => setPayload((prev) => (isExplora(prev) ? { ...prev, ...p } : prev));

  const updateVariable = (index: number, patchVar: Partial<ExploraVariable>) => {
    setPayload((prev) => {
      if (!isExplora(prev)) return prev;
      return {
        ...prev,
        availableVariables: prev.availableVariables.map((v, i) => (i === index ? { ...v, ...patchVar } : v)),
      };
    });
  };

  const addVariable = () => {
    setPayload((prev) => {
      if (!isExplora(prev)) return prev;
      const n = prev.availableVariables.length + 1;
      return {
        ...prev,
        availableVariables: [
          ...prev.availableVariables,
          { id: `var_${n}`, label: `Acción ${n}`, internalValue: 1, type: 'ADD' },
        ],
      };
    });
  };

  const removeVariable = (index: number) => {
    setPayload((prev) => {
      if (!isExplora(prev) || prev.availableVariables.length <= 1) return prev;
      return {
        ...prev,
        availableVariables: prev.availableVariables.filter((_, i) => i !== index),
      };
    });
  };

  const applySkin = (skin: ExploraSkin) => {
    setPayload((prev) => (isExplora(prev) ? applyExploraSkin(prev, skin) : prev));
  };

  const save = () => {
    try {
      validateActivityPayload('sandbox', payload);
      setError(null);
      const draft = saveActivityDraft({
        id: savedId,
        mode: 'sandbox',
        title: payload.title,
        subjectTag: subjectTag.trim() || 'matematicas',
        payload,
      });
      onSaved?.(draft);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const handleAiBuild = async (promptText: string) => {
    setAiLoading(true);
    setError(null);
    setNotice(null);
    try {
      const { draft, notice: aiNotice } = await generateSubjectAwareActivityDraft({
        mode: 'sandbox',
        subjectId: launchSubjectId,
        subjectTag: subjectTag.trim() || undefined,
        tuning: promptText,
        draftId: savedId,
        persist: false,
      });
      setPayload(draft.payload as SandboxPayload);
      if (draft.subjectTag) setSubjectTag(draft.subjectTag);
      if (aiNotice) setNotice(aiNotice);
    } catch (e) {
      setError(
        e instanceof Error && e.message === 'subject_context_required'
          ? 'Selecciona una asignatura desde Herramientas para generar con IA.'
          : e instanceof Error
            ? e.message
            : 'No se pudo generar con IA',
      );
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <StudioLiveShell
      embedded={embedded}
      error={error}
      subjectTag={subjectTag}
      onSubjectTagChange={setSubjectTag}
      onSave={save}
      aiActivityLabel="laboratorio"
      onAiBuild={handleAiBuild}
      aiLoading={aiLoading}
    >
      {notice ? (
        <p className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] text-amber-900">
          {notice}
        </p>
      ) : null}
      <div className="plugin-live-fill flex h-full min-h-0 flex-1 flex-col overflow-hidden">
        <ExploraLiveStage
          payload={payload}
          runtime={runtime}
          mode="editor"
          size="lg"
          fill
          onPayloadChange={patch}
          onVariableChange={updateVariable}
          onAddVariable={addVariable}
          onRemoveVariable={removeVariable}
          onSkinChange={applySkin}
        />
      </div>
    </StudioLiveShell>
  );
}
